import { FC, useMemo } from 'react';

import { GlossaryItem } from '../../types/Glossary';
import { GlossaryListItem } from './GlossaryListItem';

type Group = Record<string, GlossaryItem[]>;

interface Props {
  glossary: GlossaryItem[];
  handleClick: (item: GlossaryItem) => void;
}

export const GlossaryGrid: FC<Props> = ({ glossary, handleClick }) => {
  const groupBy = useMemo(() => {
    const terms = glossary.reduce((acc: Group, curr) => {
      const initial = curr.slug.charAt(0);
      if (acc[initial]) {
        acc[initial].push(curr);
      } else {
        return { ...acc, [initial]: [curr] };
      }
      return acc;
    }, {});

    const sorted: Group = Object.keys(terms)
      .sort()
      .reduce((acc, curr) => {
        const sort = terms[curr].sort((a, b) => a.slug.localeCompare(b.slug));
        return { ...acc, [curr]: sort };
      }, {});

    return sorted;
  }, [glossary]);

  return (
    <main className="flex flex-col gap-y-0 divide-y divide-divider/20">
      {Object.entries(groupBy).map(([key, items]) => (
        <section
          key={key}
          className="grid-section gap-y-6 py-6 first:pt-0 last:pb-0 md:py-9"
        >
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="text-md font-bold uppercase md:text-lg">{key}</div>
          </div>
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            <div className="grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <div key={item.slug}>
                  <GlossaryListItem
                    item={item}
                    onClick={() => handleClick(item)}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </main>
  );
};
